import { EmptyProps, usePath } from '@arnold/common';
import { FC } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import PrivateRouter from './components/PrivateRouter';
import {
  ChangePasswordScreen,
  ChooseProcessSurveyScreen,
  ChooseProcessTopicGroupScreen,
  ConfirmEmailScreen,
  CreateExitScreen,
  CreateOnboardingScreen,
  CreateProcessSurveyScreen,
  CreateSurveyScreen,
  LoginScreen,
  OrganizationsScreen,
  OverviewScreen,
  ProcessSurveyDetailScreen,
  ProcessSurveyStepsEditScreen,
  ProcessSurveysScreen,
  RecoverPasswordScreen,
  RegistrationScreen,
  ReportAccessScreen,
  SalesScreen,
  SettingsScreen,
  SurveyArchiveScreen,
  PulserSurveysScreen,
  SurveysScreen,
  SvenEditorScreen,
  SvenPreviewScreen,
  TeamsScreen,
  UpdateTeamScreen,
} from './screens';

// @todo - import me via index from ./screens
import AddRespondentFromTeamioScreen from './screens/AddRespondentFromTeamio/AddRespondentFromTeamioScreen';
import ConversationSubjectsScreen from './screens/ConversationSubjects/ConversationSubjectsScreen';
import PulserConversationSubjectsScreen from './screens/ConversationSubjects/PulserConversationSubjectsScreen';
import { ExitDetailScreen } from './screens/ExitDetail/ExitDetailScreen';
import LoginWithPasswordScreen from './screens/Login/LoginWithPasswordScreen';
import LoginWithMagicLinkConfirmScreen from './screens/LoginWithMagicLink/LoginWithMagicLinkConfirmScreen';
import SsoCallbackScreen from './screens/Sso/SsoCallbackScreen';
import LoginWithMagicLinkScreen from './screens/LoginWithMagicLink/LoginWithMagicLinkScreen';
import { OnboardingDetailScreen } from './screens/OnboardingDetail/OnboardingDetailScreen';
import { PreviewFormScreen } from './screens/PreviewForm/PreviewFormScreen';
import AddTopicScreen from './screens/Process/AddTopicScreen';
import ProcessScreen from './screens/Process/ProcessScreen';
import { ReopenSurveyScreen } from './screens/SurveyDetail/ReopenSurveyScreen';
import { SurveyDetailScreen } from './screens/SurveyDetail/SurveyDetailScreen';
import { SvenEditorPublicScreen } from './screens/SvenEditorPublic';
import TopicsScreen from './screens/Topics/TopicsScreen';
import PulserTopicsScreen from './screens/Topics/PulserTopicsScreen';
import TransactionHistoryScreen from './screens/TransactionHistory/TransactionHistoryScreen';

export const Routes: FC<EmptyProps> = () => {
  const { processStepEditPath } = usePath();

  return (
    <Switch>
      <Route path="/login/:lang?" component={LoginScreen} />
      <Route path="/loginWithPassword/:lang?" component={LoginWithPasswordScreen} />
      <Route path="/loginWithMagicLink/:lang?" component={LoginWithMagicLinkScreen} />
      <Route path="/loginWithMagicLinkConfirm/:accessToken/:lang?" component={LoginWithMagicLinkConfirmScreen} />
      <Route path="/sso-callback" component={SsoCallbackScreen} />
      <Route path="/recoverPassword/:lang?" component={RecoverPasswordScreen} />
      <Route path="/changePassword/:accessToken/:lang?" component={ChangePasswordScreen} />
      <Route path="/register/confirm/:accessToken/:lang?" component={ConfirmEmailScreen} />
      <Route path="/register/:lang?" component={RegistrationScreen} />
      <Route path="/sales" component={SalesScreen} />
      <Route path="/preview/:lang?" component={PreviewFormScreen} />
      <Route path="/svenEditorPublic/:topicGroupId/:lang?" component={SvenEditorPublicScreen} />
      <Route path="/svenPreviewPublic/:topicGroupId/:lang?" component={SvenEditorPublicScreen} />
      <PrivateRouter
        // this should be optimized, path should know his own parameters name
        path={processStepEditPath.createPath(':processTopicGroupId')}
        component={ProcessSurveyStepsEditScreen}
      />
      <PrivateRouter path="/overview/:surveyGroupId?" component={OverviewScreen} />
      <PrivateRouter path="/surveys/:status?" component={SurveysScreen} />
      <PrivateRouter path="/pulser/topics" component={PulserTopicsScreen} exact={true} />
      <PrivateRouter path="/pulser/:status?" component={PulserSurveysScreen} />
      <PrivateRouter path="/teams" component={TeamsScreen} />
      <PrivateRouter
        path="/addRespondentFromTeamio"
        component={AddRespondentFromTeamioScreen}
        leftNavbarOnly
        hideSidebar
        disableSavingAccessToken
      />
      <PrivateRouter path="/updateTeam" component={UpdateTeamScreen} />
      <PrivateRouter path="/settings/transactionHistory" component={TransactionHistoryScreen} exact={true} />
      <PrivateRouter path="/settings" component={SettingsScreen} />
      <PrivateRouter path="/reportAccess/:surveyId" component={ReportAccessScreen} />
      <PrivateRouter path="/createSurvey/:subjectType/:subjectId/:subjectLabel" component={CreateSurveyScreen} />
      <PrivateRouter path="/organizations" component={OrganizationsScreen} />
      <PrivateRouter path="/process/:topicGroupId/step/add" component={AddTopicScreen} />
      <PrivateRouter path="/process/:topicGroupId" component={ProcessScreen} />
      <PrivateRouter path="/conversationSubjects" component={ConversationSubjectsScreen} exact={true} />
      <PrivateRouter path="/pulserConversationSubjects" component={PulserConversationSubjectsScreen} exact={true} />
      <PrivateRouter path="/topics" component={TopicsScreen} />
      <PrivateRouter path="/surveyDetail/:surveyId" component={SurveyDetailScreen} exact={true} />
      <PrivateRouter path="/reopenSurvey/:surveyId" component={ReopenSurveyScreen} exact={true} />
      <PrivateRouter path="/surveyDetail/:surveyId/:type" component={SurveyDetailScreen} />
      <PrivateRouter path="/onboarding/:surveyGroupId?/:type?" component={OnboardingDetailScreen} />
      <PrivateRouter path="/processSurvey/:surveyGroupId/:type?" component={ProcessSurveyDetailScreen} />
      <PrivateRouter path="/processSurveyConversationSubjects" component={ChooseProcessSurveyScreen} />
      <PrivateRouter path="/createOnboarding/:subjectId" component={CreateOnboardingScreen} />
      <PrivateRouter path="/createProcessSurvey/:subjectId" component={CreateProcessSurveyScreen} />
      <PrivateRouter path="/createExit/:subjectId" component={CreateExitScreen} />
      <PrivateRouter path="/processSurveys" component={ProcessSurveysScreen} />
      <PrivateRouter path="/exit/:surveyGroupId?/:type?" component={ExitDetailScreen} />
      <PrivateRouter path="/conversationSubjects/:type?" component={ChooseProcessTopicGroupScreen} />
      <PrivateRouter path="/archivedSurveyDetail/:surveyId" component={SurveyDetailScreen} exact={true} />
      <PrivateRouter path="/archivedOnboarding/:surveyGroupId" component={OnboardingDetailScreen} exact={true} />
      <PrivateRouter path="/archivedProcessSurvey/:surveyGroupId" component={ProcessSurveyDetailScreen} exact={true} />
      <PrivateRouter path="/surveyArchive" component={SurveyArchiveScreen} />
      <PrivateRouter
        path="/topicEditorPreview/:topicGroupId/:languageId?"
        component={SvenPreviewScreen}
        exact={true}
        leftNavbarOnly
      />
      <PrivateRouter
        path="/topicEditor/:topicGroupId/:languageId?"
        component={SvenEditorScreen}
        exact={true}
        leftNavbarOnly
      />
      <Redirect from="/" to="overview" />
    </Switch>
  );
};
